import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { HeartPulse, TrendingUp, AlertTriangle, CheckCircle } from "lucide-react";

export default function ValuationHealth() {
  const { API } = useAuth();
  const [healthData, setHealthData] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/valuation-health`, { credentials: "include" });
      if (res.ok) setHealthData(await res.json());
    } catch (e) { console.error(e); }
  }, [API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const scoreConfig = {
    excellent: { icon: CheckCircle, color: "text-[#D4AF37]", bg: "bg-[#D4AF37]/10", label: "Excellent" },
    good: { icon: TrendingUp, color: "text-[#556B2F]", bg: "bg-[#556B2F]/10", label: "Good" },
    needs_review: { icon: AlertTriangle, color: "text-[#F59E0B]", bg: "bg-[#F59E0B]/10", label: "Needs Review" },
  };

  const excellentCount = healthData.filter((h) => h.health_score === "excellent").length;
  const goodCount = healthData.filter((h) => h.health_score === "good").length;
  const reviewCount = healthData.filter((h) => h.health_score === "needs_review").length;

  return (
    <div data-testid="valuation-health-page">
      <div className="mb-6">
        <h2 className="font-serif text-2xl text-white">Valuation Health</h2>
        <p className="text-white/40 text-sm font-sans">{healthData.length} properties assessed</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          { label: "Excellent", count: excellentCount, color: "#D4AF37" },
          { label: "Good", count: goodCount, color: "#556B2F" },
          { label: "Needs Review", count: reviewCount, color: "#F59E0B" },
        ].map((s) => (
          <div key={s.label} className="bg-[#11130F] border border-white/10 rounded-sm p-5">
            <div className="w-2 h-2 rounded-full mb-3" style={{ backgroundColor: s.color }} />
            <p className="font-serif text-3xl text-white">{s.count}</p>
            <p className="text-white/40 text-xs font-sans mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {healthData.length === 0 ? (
        <div className="bg-[#11130F] border border-white/10 rounded-sm p-12 text-center">
          <HeartPulse className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-sans">No valuation data available. Add properties first.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {healthData.map((h) => {
            const sc = scoreConfig[h.health_score] || scoreConfig.needs_review;
            return (
              <div key={h.property_id} className="bg-[#11130F] border border-white/10 rounded-sm p-5 hover:border-[#D4AF37]/40 transition-colors flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <HeartPulse className="w-5 h-5 text-[#D4AF37]" />
                  <div>
                    <h4 className="font-sans font-medium text-white text-sm">{h.name}</h4>
                    <p className="text-white/40 text-xs font-sans">Valuation: ${h.valuation?.toLocaleString()}</p>
                  </div>
                </div>
                <span className={`${sc.bg} ${sc.color} text-xs px-3 py-1 rounded-sm font-sans flex items-center gap-1`}>
                  <sc.icon className="w-3 h-3" />
                  {sc.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
